import { useRef, useState, useCallback } from "react";
import { useCommentDraft } from "@/hooks/useCommentDraft";
import DOMPurify from "dompurify";
import { Send, Paperclip, X, ExternalLink, Loader2, MessageSquare } from "lucide-react";
import { openPendingFile } from "../../../utils/download";
import { Spinner } from "@/components/ui/Spinner";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { useQueryClient } from "@tanstack/react-query";
import {
  useSimulationComments,
  useAddSimulationComment,
} from "@/features/finances/hooks/useSimulationDiscussion";
import { simulationDiscussionApi } from "@/features/finances/api/simulationDiscussion";
import { useAuthStore } from "@/store/auth";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import type { SimulationAttachment } from "@/features/finances/types/simulation";
import type { MentionSuggestion } from "@/components/ui/RichTextEditor";

interface Props {
  simulationId: string;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function AttachmentList({ attachments }: { attachments: SimulationAttachment[] }) {
  if (!attachments.length) return null;
  return (
    <ul className="mt-1.5 flex flex-wrap gap-1.5">
      {attachments.map((a) => (
        <li key={a.id}>
          <a
            href={a.url ?? "#"}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] px-2 py-0.5 rounded"
          >
            <ExternalLink size={10} className="flex-shrink-0" />
            <span className="max-w-[160px] truncate">{a.original_name}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
    ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
  });
}

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function CommentContent({ content }: { content: string }) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const { data: allUsers = [] } = useInternalUsers();
  const names = allUsers.map((u) => u.full_name);

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "IMG") {
      setLightboxSrc((target as HTMLImageElement).src);
    }
  }, []);

  if (isHtmlContent(content)) {
    return (
      <>
        <div
          className="rich-text-content"
          dangerouslySetInnerHTML={{ __html: highlightMentionsInHtml(sanitize(content), names) }}
          onClick={handleClick}
        />
        {lightboxSrc && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
            onClick={() => setLightboxSrc(null)}
          >
            <img
              src={lightboxSrc}
              alt=""
              className="max-w-[90vw] max-h-[90vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              className="absolute top-4 right-4 text-white text-2xl font-bold hover:text-gray-300"
              onClick={() => setLightboxSrc(null)}
            >
              ×
            </button>
          </div>
        )}
      </>
    );
  }

  return (
    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
      {highlightMentions(content, names)}
    </p>
  );
}

export function SimulationDiscussion({ simulationId }: Props) {
  const { data: comments = [], isLoading } = useSimulationComments(simulationId);
  const { mutateAsync: addComment, isPending: sending } = useAddSimulationComment(simulationId);
  const qc = useQueryClient();
  const currentUser = useAuthStore((s) => s.user);
  const { data: allUsers = [] } = useInternalUsers();

  // Oznaczać można TYLKO osoby z dostępem do symulacji (CEO) — pozostali nie
  // pojawiają się w podpowiedziach @ (backend i tak odrzuca nieuprawnionych).
  const mentionSuggestions: MentionSuggestion[] = allUsers
    .filter((u) => u.is_ceo)
    .map((u) => ({
      id: u.id,
      name: u.full_name,
      role: u.role,
      is_ceo: u.is_ceo,
    }));

  const [draft, setDraft, clearDraft] = useCommentDraft(`comment-draft-sim-${simulationId}`);
  const [composerMentionIds, setComposerMentionIds] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleSend() {
    const content = draft.trim();
    if (!content) return;
    try {
      const comment = await addComment({ content, mentionIds: composerMentionIds });
      if (pendingFiles.length > 0) {
        setUploadingFor(comment.id);
        for (const file of pendingFiles) {
          await simulationDiscussionApi.uploadCommentAttachment(simulationId, comment.id, file);
        }
        await qc.invalidateQueries({ queryKey: ["order-simulations", simulationId, "comments"] });
        setUploadingFor(null);
      }
      clearDraft();
      setComposerMentionIds([]);
      setPendingFiles([]);
    } catch {
      // errors surfaced by query invalidation
    }
  }

  async function handleImageUpload(file: File): Promise<string> {
    return simulationDiscussionApi.uploadCommentImage(simulationId, file);
  }

  function addFiles(files: FileList) {
    setPendingFiles((prev) => [...prev, ...Array.from(files)]);
  }

  const busy = sending || uploadingFor !== null;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 sm:p-5 print:hidden">
      <div className="mb-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <MessageSquare size={16} className="text-[var(--color-primary)]" /> Dyskusja
        </h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 pl-6">
          Wspólna dla całej symulacji — niezależna od wybranego wariantu.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {isLoading && (
          <div className="flex justify-center py-4">
            <Spinner size="sm" />
          </div>
        )}

        {!isLoading && comments.length === 0 && (
          <p className="text-sm text-gray-400 dark:text-gray-500 italic">
            Brak komentarzy. Napisz pierwszy.
          </p>
        )}

        {comments.map((c) => {
          const isOwn = currentUser?.id === c.author_id;
          return (
            <div
              key={c.id}
              className="flex gap-3 border-l-4 border-[var(--color-primary)] bg-gray-50 dark:bg-gray-800/60 rounded-r-lg px-3 py-2.5"
            >
              {c.author_photo_url ? (
                <img
                  src={c.author_photo_url}
                  alt={c.author_name}
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[var(--color-primary)] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mt-0.5">
                  {(c.author_name || "?")
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()}
                </div>
              )}

              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 mb-1 flex-wrap">
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {c.author_name}
                  </span>
                  {isOwn && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">Ty</span>
                  )}
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto whitespace-nowrap">
                    {formatDate(c.created_at)}
                  </span>
                </div>

                <CommentContent content={c.content} />

                {uploadingFor === c.id && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 flex items-center gap-1">
                    <Loader2 size={10} className="animate-spin" /> Przesyłanie załączników…
                  </p>
                )}

                <AttachmentList attachments={c.attachments} />
              </div>
            </div>
          );
        })}

        {/* Composer */}
        <div className="flex flex-col gap-2 mt-1 border-t border-gray-100 dark:border-gray-700 pt-3">
          <RichTextEditor
            value={draft}
            onChange={setDraft}
            disabled={busy}
            placeholder="Napisz komentarz… (wpisz @ aby oznaczyć)"
            onImageUpload={handleImageUpload}
            mentionSuggestions={mentionSuggestions}
            onMentionIdsChange={setComposerMentionIds}
          />

          {pendingFiles.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {pendingFiles.map((f, i) => (
                <li
                  key={i}
                  className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300"
                >
                  <button
                    type="button"
                    onClick={() => openPendingFile(f)}
                    className="max-w-[130px] truncate hover:underline hover:text-[var(--color-primary)]"
                    title={`${f.name} — kliknij, aby podejrzeć`}
                  >
                    {f.name}
                  </button>
                  <button
                    onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X size={10} />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="flex items-center justify-between">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-[var(--color-primary)] disabled:opacity-40"
            >
              <Paperclip size={13} />
              Załącz plik
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => e.target.files && addFiles(e.target.files)}
            />

            <button
              onClick={handleSend}
              disabled={busy || !draft.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40 transition-colors"
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
              Wyślij
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
